// ABC SEO/GEO — app shell. Composes sidebar, top bar, routed screens.
const { Icon } = window.SGIcons;

const PLACEHOLDERS = {
  settings: { icon: 'settings', title: 'Settings', body: 'Manage projects, team access, integrations, and your white-label branding.' },
};

function Placeholder({ p }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '90px 20px', textAlign: 'center', animation: 'sg-fade 0.3s var(--sg-ease)' }}>
      <span style={{ width: 64, height: 64, borderRadius: 'var(--sg-radius-xl)', background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={p.icon} size={30} />
      </span>
      <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 22, color: 'var(--sg-text)' }}>{p.title}</div>
      <div style={{ fontSize: 14, color: 'var(--sg-text-2)', maxWidth: 440, lineHeight: 1.55 }}>{p.body}</div>
      <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '0 22px', marginTop: 6, border: 'none',
        borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14,
        color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: '0 4px 14px rgba(37,99,235,0.32)' }}>
        <Icon name="plus" size={16} /> Get started
      </button>
    </div>
  );
}

function ReauthBanner() {
  const need = window.SGLive.useReauth();
  const [busy, setBusy] = React.useState(false);
  if (!need) return null;
  const reconnect = () => { setBusy(true); window.SGLive.reconnectGoogle().then(() => setBusy(false)); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', background: 'var(--sg-amber-50)', borderBottom: '1px solid var(--sg-amber-100, #F6E6C7)' }}>
      <Icon name="alert-triangle" size={17} color="var(--sg-amber)" />
      <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text)' }}>
        <strong>Google needs reconnecting.</strong> Your Search Console / Analytics access expired (Google does this periodically). Reconnect to restore live data.
      </div>
      <button onClick={reconnect} disabled={busy} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>
        <Icon name="refresh-cw" size={14} /> {busy ? 'Opening…' : 'Reconnect Google'}
      </button>
    </div>
  );
}

// Trial status + upgrade CTA. Shown to self-serve / trial accounts only; hidden
// for paid (status active), admins, and GHL-scoped users. Links to the public
// pricing page — paying with the same email upgrades this account in place.
function TrialBanner() {
  const ent = window.SGLive.useEntitlement();
  if (ent.loading || ent.admin || ent.status !== 'trial') return null;
  const now = Math.floor(Date.now() / 1000);
  const secs = ent.trialEndsAt ? ent.trialEndsAt - now : null;
  const days = secs != null ? Math.max(0, Math.ceil(secs / 86400)) : null;
  const expired = secs != null && secs <= 0;
  const label = expired ? 'Your free trial has ended'
    : days != null ? `${days} day${days === 1 ? '' : 's'} left in your free trial`
    : 'You’re on a free trial';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px',
      background: expired ? 'var(--sg-red-50, #FEF2F2)' : 'var(--sg-violet-50, #F5F3FF)',
      borderBottom: `1px solid ${expired ? 'var(--sg-red-100, #FCD9D9)' : 'var(--sg-violet-100, #E9E3FF)'}` }}>
      <Icon name={expired ? 'alert-triangle' : 'sparkles'} size={17} color={expired ? 'var(--sg-red, #DC2626)' : 'var(--sg-violet-600)'} />
      <div style={{ flex: 1, fontSize: 13, color: 'var(--sg-text)' }}>
        <strong>{label}.</strong> Keyword research, backlinks & competitor data unlock on a paid plan.
      </div>
      <button onClick={() => window.open('https://aibizconnect.ca/seo-geo', '_blank', 'noopener')}
        style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-violet)' }}>
        <Icon name="zap" size={14} /> Upgrade
      </button>
    </div>
  );
}

function SGApp() {
  const [route, setRoute] = React.useState('dashboard');
  const [collapsed, setCollapsed] = React.useState(false);
  const ent = window.SGLive.useEntitlement();
  const isAdmin = ent.admin;

  // If a non-admin somehow lands on the admin route, bounce to dashboard.
  React.useEffect(() => { if (route === 'admin' && ent.loading === false && !isAdmin) setRoute('dashboard'); }, [route, isAdmin, ent.loading]);

  let screen;
  if (route === 'admin') screen = isAdmin ? <window.SGAdmin /> : <window.SGDashboard />;
  else
  if (route === 'dashboard') screen = <window.SGDashboard />;
  else if (route === 'geo') screen = <window.SGGeoVisibility />;
  else if (route === 'agent') screen = <window.SGAgentReadiness />;
  else if (route === 'audit') screen = <window.SGSiteAudit />;
  else if (route === 'keywords') screen = <window.SGKeywords />;
  else if (route === 'content') screen = <window.SGContent />;
  else if (route === 'search') screen = <window.SGSearchPerformance />;
  else if (route === 'rank') screen = <window.SGRankTracking />;
  else if (route === 'competitors') screen = <window.SGCompetitors />;
  else if (route === 'backlinks') screen = <window.SGBacklinks />;
  else if (route === 'video') screen = <window.SGVideo />;
  else if (route === 'listings') screen = <window.SGListings />;
  else if (route === 'reports') screen = <window.SGReports />;
  else if (route === 'settings' || route === 'connections') screen = <window.SGConnections />;
  else screen = <Placeholder p={PLACEHOLDERS[route]} />;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--sg-page)' }}>
      <window.SGSidebar active={route} onNavigate={setRoute} collapsed={collapsed} admin={isAdmin} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <window.SGTopBar onToggleSidebar={() => setCollapsed((c) => !c)} onNewAudit={() => setRoute('audit')} onNavigate={setRoute} />
        <ReauthBanner />
        <TrialBanner />
        <main style={{ flex: 1, overflow: 'auto', padding: '26px 28px 48px' }}>{screen}</main>
      </div>
    </div>
  );
}

window.SGApp = SGApp;
